import { useCallback, useRef, useState } from "react";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "@/lib/appToast";
import { StandardModal } from "@/components/StandardModal";
import { MODAL_BODY_CLASS } from "@/lib/modalStyles";
import { getTodaySaoPaulo } from "@/lib/dateUtils";
import { getNfeXmlMaxBytes, parseNfeFornecedorXml, validateNfeXmlFile } from "@/lib/nfeXmlParser";

export type FornecedorNfPrefill = {
  fornecedor: string;
  valor: string;
  dataLancamento: string;
  dataVencimento: string;
};

export function ModalImportarNfFornecedor({
  open,
  onClose,
  onParsed,
}: {
  open: boolean;
  onClose: () => void;
  onParsed: (prefill: FornecedorNfPrefill) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");

  const reset = useCallback(() => {
    dragDepth.current = 0;
    setIsDragging(false);
    setLoading(false);
    setSelectedFileName("");
    if (fileRef.current) fileRef.current.value = "";
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleFile = useCallback(
    async (file: File | null) => {
      if (!file) return;
      const invalid = validateNfeXmlFile(file);
      if (invalid) {
        toast.error(invalid);
        return;
      }
      setSelectedFileName(file.name);
      setLoading(true);
      try {
        const xmlText = await file.text();
        const parsed = parseNfeFornecedorXml(xmlText);
        onParsed({
          fornecedor: parsed.fornecedor,
          valor: String(parsed.valor),
          dataLancamento: getTodaySaoPaulo(),
          dataVencimento: parsed.dataVencimento,
        });
        handleClose();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Erro ao ler XML da NF-e.");
      } finally {
        setLoading(false);
      }
    },
    [handleClose, onParsed],
  );

  const openFilePicker = useCallback(() => {
    if (loading) return;
    fileRef.current?.click();
  }, [loading]);

  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragging(true);
    }
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDragging(false);
    }
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth.current = 0;
      setIsDragging(false);
      if (loading) return;
      const f = e.dataTransfer.files?.[0];
      void handleFile(f ?? null);
    },
    [handleFile, loading],
  );

  return (
    <StandardModal
      open={open}
      title="Enviar NF"
      titleId="nf-import-title"
      onClose={handleClose}
      panelClassName="gs-modal-panel-max max-w-xl max-h-[90vh]"
      closeDisabled={loading}
    >
      <div className={MODAL_BODY_CLASS}>
        <input
          id="nf-xml-file"
          ref={fileRef}
          type="file"
          accept=".xml"
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            const f = e.target.files?.[0];
            void handleFile(f ?? null);
            e.target.value = "";
          }}
        />
        <div
          role="button"
          tabIndex={0}
          onClick={(ev) => {
            ev.stopPropagation();
            openFilePicker();
          }}
          onKeyDown={(ev) => {
            if (ev.key === "Enter" || ev.key === " ") {
              ev.preventDefault();
              openFilePicker();
            }
          }}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
          className={`relative flex min-h-[176px] flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors outline-none focus:outline-none focus-visible:outline-none ${
            loading
              ? "pointer-events-none cursor-wait border-gray-200 bg-gray-50 text-gray-500"
              : isDragging
                ? "cursor-copy border-client-primary bg-client-secondary text-gray-900"
                : "cursor-pointer border-gray-200 bg-gray-50/90 hover:border-gray-300 hover:bg-gray-50"
          }`}
          aria-busy={loading}
          aria-label="Enviar XML de NF-e: arraste o arquivo aqui ou clique para selecionar. Somente arquivos .xml."
        >
          {loading ? (
            <>
              <Loader2 className="h-10 w-10 shrink-0 animate-spin text-client-primary" aria-hidden />
              <p className="text-sm font-medium text-gray-800">Lendo XML da NF-e…</p>
              {selectedFileName ? <p className="max-w-full break-all text-xs text-gray-600">{selectedFileName}</p> : null}
            </>
          ) : (
            <>
              <FileUp className="h-10 w-10 shrink-0 text-gray-400" strokeWidth={1.25} aria-hidden />
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-800">Arraste o XML da NF-e aqui</p>
                <p className="text-sm text-gray-500">ou clique para selecionar</p>
              </div>
              {selectedFileName ? <p className="max-w-full break-all pt-1 text-xs font-medium text-gray-700">{selectedFileName}</p> : null}
              <p className="text-xs text-gray-400">Apenas .xml · até {Math.round(getNfeXmlMaxBytes() / (1024 * 1024))} MB</p>
            </>
          )}
        </div>
      </div>
    </StandardModal>
  );
}
